import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';

interface HistoryLink {
  url: string;
  status: string;
  created_at?: string;
  dateTime?: string;
  dateTime_formatted?: string;
  id?: number | string;
}

interface HistoryStats {
  submitted: number;
  pending: number;
  rejected: number;
  drip_scheduled: number;
  total: number;
  successRate: number;
}

interface HistoryResponse {
  success: boolean;
  data: {
    links: HistoryLink[];
    submitted?: number;
    pending?: number;
    rejected?: number;
    drip_scheduled?: number;
    total?: number;
    hasMore: boolean;
  };
}

interface DripJob {
  url?: string;
  urls?: string;
  countdown?: string;
  ready: boolean;
  time_left_seconds: number;
  time_left_formatted: string;
}

interface DripResponse {
  success: boolean;
  data: {
    jobs: DripJob[];
  };
}

@Component({
  selector: 'app-history',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './history.html',
  styleUrls: ['./history.scss']
})
export class HistoryComponent implements OnInit {
  links: HistoryLink[] = [];
  stats: HistoryStats = { submitted: 0, pending: 0, rejected: 0, drip_scheduled: 0, total: 0, successRate: 0 };
  currentOffset = 0;
  limit = 50;
  hasMore = false;
  isLoading = false;
  isLoadingMore = false;
  dripJobs: DripJob[] = [];
  countdownInterval: ReturnType<typeof setInterval> | null = null;
  autoRefreshInterval: ReturnType<typeof setInterval> | null = null;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  // Search & Filter properties
  searchQuery = '';
  statusFilter = '';

  // Animation properties
  animatedTotal = 0;
  animatedSubmitted = 0;
  animatedPending = 0;
  recentActivity: HistoryLink[] = [];

  // Animation intervals
  private animationIntervals: ReturnType<typeof setTimeout>[] = [];

  constructor(private http: HttpClient) { }

  ngOnInit() {
    this.loadHistory();
    this.loadDripStatus();
    this.startAutoRefresh();
    this.startCountdown();
  }

  loadHistory(reset: boolean = true) {
    if (reset) {
      this.isLoading = true;
      this.currentOffset = 0;
      this.links = [];
      this.hasMore = true; // Reset hasMore on new search
    } else {
      if (!this.hasMore) return; // Prevent unnecessary calls
      this.isLoadingMore = true;
    }

    const token = localStorage.getItem('auth_token');

    const offset = Math.max(0, this.currentOffset);
    const limit = Math.max(1, Math.min(100, this.limit));

    // Construct query parameters
    let params = `offset=${offset}&limit=${limit}`;
    if (this.searchQuery.trim()) {
      params += `&search=${encodeURIComponent(this.searchQuery.trim())}`;
    }
    if (this.statusFilter) {
      params += `&status=${encodeURIComponent(this.statusFilter)}`;
    }

    console.log('Requesting links:', params);

    this.http.get<HistoryResponse>(`${environment.apiUrl}/user/links-simple.php?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).subscribe({
      next: (response) => {
        if (response.success) {

          if (reset) {
            this.links = response.data.links || [];
          } else {
            this.links = [...this.links, ...(response.data.links || [])];
          }

          // We don't need to apply filters client-side anymore
          // this.applyFilters();

          // Use API calculated stats
          // Note: total stats might be for ALL links, not filtered ones, depending on dashboard requirements.
          // But here we want filtered view pagination.

          const submitted = response.data.submitted || 0;
          const pending = response.data.pending || 0;
          const rejected = response.data.rejected || 0;
          const processedTotal = submitted + pending + rejected;

          this.stats = {
            submitted: submitted,
            pending: pending,
            rejected: rejected,
            drip_scheduled: response.data.drip_scheduled || 0,
            total: response.data.total || 0, // This is now filtered total from API
            successRate: processedTotal > 0 ? Math.round((submitted / processedTotal) * 100) : 0
          };

          // hasMore logic: check if we have fetched less than total (filtered)
          // OR if the current batch was full size (simple heuristic)
          this.hasMore = response.data.hasMore;
          this.currentOffset += this.limit;
        }
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.isLoadingMore = false;
        this.showToast('Failed to load history data', 'error');
        console.error('History load error:', error);
      }
    });
  }

  startAutoRefresh() {
    // Auto-refresh every 60 seconds (less intrusive)
    this.autoRefreshInterval = setInterval(() => {
      this.loadHistory(true);
      this.loadDripStatus();
    }, 60000);
  }

  loadMore() {
    if (this.hasMore && !this.isLoadingMore) {
      this.loadHistory(false);
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  getStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'submitted': 'submitted',
      'pending': 'pending',
      'rejected': 'rejected',
      'drip scheduled': 'drip-scheduled'
    };
    return `status-${statusMap[status.toLowerCase()] || 'pending'}`;
  }

  getDripJobStatus(url: string): string {
    const job = this.dripJobs.find(j => j.url === url);
    // If no job found in pending queue, it was completed
    if (!job) return 'drip-completed';
    // If job exists, check if it's ready or still pending
    return job.ready || job.time_left_seconds <= 0 ? 'drip-ready' : 'drip-pending';
  }

  getDripJobStatusText(url: string): string {
    const job = this.dripJobs.find(j => j.url === url);
    // If no job found in pending queue, it means it was completed
    if (!job) return 'Completed';
    // If job exists in pending queue, check if it's ready or still pending
    return job.ready || job.time_left_seconds <= 0 ? 'Ready' : 'Pending';
  }

  getDripCountdown(url: string): string {
    const job = this.dripJobs.find(j => j.url === url);
    if (!job || job.time_left_seconds <= 0) return '';
    return job.time_left_formatted;
  }

  loadDripStatus() {
    this.http.get<DripResponse>(`${environment.apiUrl}/user/drip-status-simple-auth.php`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.dripJobs = response.data.jobs || [];
        }
      },
      error: (error: HttpErrorResponse) => {
        this.dripJobs = [];
        this.showToast('Failed to load drip status', 'error');
        console.error('Drip status error:', error);
      }
    });
  }

  startCountdown() {
    this.countdownInterval = setInterval(() => {
      let needsRefresh = false;

      this.dripJobs.forEach((job) => {
        if (job.time_left_seconds > 0) {
          job.time_left_seconds--;
          const hours = Math.floor(job.time_left_seconds / 3600);
          const minutes = Math.floor((job.time_left_seconds % 3600) / 60);
          const seconds = job.time_left_seconds % 60;
          job.time_left_formatted =
            `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

          if (job.time_left_seconds <= 0) {
            job.ready = true;
            job.time_left_formatted = '00:00:00';
            needsRefresh = true;
          }
        }
      });

      // Remove jobs that have been ready for more than 30 seconds (likely processed)
      this.dripJobs = this.dripJobs.filter(job => {
        if (job.ready && job.time_left_seconds <= -30) {
          needsRefresh = true;
          return false;
        }
        return true;
      });

      if (needsRefresh) {
        setTimeout(() => {
          this.loadHistory(true);
          this.loadDripStatus();
        }, 2000);
      }
    }, 1000);
  }

  refreshData() {
    this.loadHistory(true);
    this.loadDripStatus();
    this.showToast('Data refreshed successfully', 'success');
  }

  copyUrl(url: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        this.showToast('URL copied to clipboard', 'success');
      }).catch(() => {
        this.showToast('Failed to copy URL', 'error');
      });
    } else {
      this.showToast('Clipboard not supported', 'error');
    }
  }

  openUrl(url: string): void {
    if (!url) return;
    const normalized = url.startsWith('http') ? url : 'https://' + url;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  }

  truncUrl(url: string): string {
    return this.truncateUrl(url);
  }

  truncateUrl(url: string): string {
    if (!url) return '';
    if (url.length <= 60) return url;
    return url.substring(0, 57) + '...';
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => this.closeToast(), 3000);
  }

  closeToast() {
    this.toastMessage = '';
  }

  // Search & Filter methods
  onSearchChange() {
    this.loadHistory(true);
  }

  onFilterChange() {
    this.loadHistory(true);
  }

  clearSearch() {
    this.searchQuery = '';
    this.loadHistory(true);
  }

  clearStatusFilter() {
    this.statusFilter = '';
    this.loadHistory(true);
  }

  clearFilters() {
    this.searchQuery = '';
    this.statusFilter = '';
    this.loadHistory(true);
  }

  hasActiveFilters(): boolean {
    return this.searchQuery.trim() !== '' || this.statusFilter !== '';
  }

  private applyFilters() {
    // Legacy client-side filtering removed. Handled by loadHistory(true) on the server.
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  }
}